import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Camera, 
  Trash2, 
  Edit2, 
  ImageIcon, 
  FolderOpen,
  ExternalLink,
  Plus,
  Settings,
  Upload,
  Eye,
  EyeOff,
  Lock,
  Calendar,
  LogOut,
  ChevronUp,
  ChevronDown,
  Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [stats, setStats] = useState({ total_albums: 0, total_photos: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [heroImages, setHeroImages] = useState([]);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [showCounter, setShowCounter] = useState(true);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const heroInputRef = useRef(null);

  const fetchAlbums = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/albums`);
      setAlbums(response.data);
    } catch (error) {
      toast.error(t('failedToLoad'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchAlbumPhotos = async (code) => {
    try {
      // Admin always sees ALL photos including hidden
      const response = await axios.get(`${API}/albums/${code}/photos?include_hidden=true`);
      setAlbumPhotos(response.data);
    } catch (error) {
      toast.error(t('failedToLoad'));
    }
  };

  useEffect(() => {
    fetchAlbums();
    fetchStats();
    fetchHeroImage();
    fetchShowCounter();
  }, [fetchAlbums, fetchStats]);

  const fetchHeroImage = async () => {
    try {
      const response = await axios.get(`${API}/settings/hero-image`);
      if (response.data.images) {
        setHeroImages(response.data.images);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchShowCounter = async () => {
    try {
      const response = await axios.get(`${API}/settings/show-counter`);
      setShowCounter(response.data.show);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleCounter = async (checked) => {
    try {
      await axios.post(`${API}/admin/settings/show-counter?show=${checked}`);
      setShowCounter(checked);
      toast.success(checked ? t('counterEnabled') : t('counterDisabled'));
    } catch (error) {
      toast.error(t('failedToUpdate'));
    }
  };

  const handleUploadHeroImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('selectImagesOnly'));
      return;
    }

    if (heroImages.length >= 3) {
      toast.error(t('maxImagesReached'));
      return;
    }

    setUploadingHero(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/settings/hero-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setHeroImages(response.data.images);
      toast.success(t('imageUpdated'));
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t('maxImagesReached'));
      } else {
        toast.error(t('failedToUploadImage'));
      }
      console.error(error);
    } finally {
      setUploadingHero(false);
      e.target.value = '';
    }
  };

  const handleDeleteHeroImage = async (index) => {
    try {
      const response = await axios.delete(`${API}/admin/settings/hero-image/${index}`);
      setHeroImages(response.data.images);
      toast.success(t('imageDeleted'));
    } catch (error) {
      toast.error(t('failedToDelete'));
      console.error(error);
    }
  };

  const handleMoveHeroImage = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Validate bounds
    if (newIndex < 0 || newIndex >= heroImages.length) return;
    
    // Create new order array
    const newOrder = heroImages.map((_, i) => i);
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    
    try {
      const response = await axios.put(`${API}/admin/settings/hero-image/reorder`, newOrder, {
        withCredentials: true
      });
      setHeroImages(response.data.images);
      toast.success(t('orderUpdated') || 'Orden actualizado');
    } catch (error) {
      toast.error(t('failedToUpdate'));
      console.error(error);
    }
  };

  const handleDeleteAlbum = async (code) => {
    try {
      await axios.delete(`${API}/albums/${code}`);
      setAlbums(albums.filter(a => a.share_code !== code));
      toast.success(t('albumDeleted'));
      fetchStats();
    } catch (error) {
      toast.error(t('failedToDelete'));
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/albums/${selectedAlbum.share_code}/photos/${photoId}`);
      setAlbumPhotos(albumPhotos.filter(p => p.id !== photoId));
      setAlbums(albums.map(a => 
        a.share_code === selectedAlbum.share_code 
          ? { ...a, photo_count: a.photo_count - 1 }
          : a
      ));
      setPhotoToDelete(null);
      toast.success(t('photoDeleted'));
      fetchStats();
    } catch (error) {
      toast.error(t('failedToDelete'));
    }
  };

  const handleTogglePhotoVisibility = async (photo) => {
    try {
      await axios.patch(`${API}/albums/${selectedAlbum.share_code}/photos/${photo.id}/toggle-visibility`);
      setAlbumPhotos(albumPhotos.map(p => 
        p.id === photo.id ? { ...p, hidden: !p.hidden } : p
      ));
      toast.success(photo.hidden ? t('photoShown') : t('photoHiddenAdmin'));
    } catch (error) {
      toast.error(t('failedToUpdate'));
    }
  };

  const handleToggleOfficial = async (photo) => {
    try {
      const response = await axios.patch(`${API}/albums/${selectedAlbum.share_code}/photos/${photo.id}/toggle-official`);
      setAlbumPhotos(albumPhotos.map(p => 
        p.id === photo.id ? { ...p, is_official: response.data.is_official } : p
      ));
      toast.success(response.data.is_official ? t('markAsOfficial') : t('removeOfficial'));
    } catch (error) {
      toast.error(t('failedToUpdate'));
    }
  };

  const openAlbumPhotos = (album) => {
    setSelectedAlbum(album);
    fetchAlbumPhotos(album.share_code);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/50 h-16 flex items-center px-4 md:px-8">
        <div className="flex items-center justify-between w-full max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <img src="/mf-logo.png" alt="MFcreativemanager" className="w-8 h-8 object-contain" />
              <span className="font-bold text-lg text-slate-900 hidden sm:block" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('appName')}
              </span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="minimal" />
            <Button 
              onClick={() => navigate('/admin/create')}
              data-testid="create-album-btn"
              className="rounded-full bg-gradient-to-r from-indigo-600 to-pink-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('createAlbum')}
            </Button>
            <Button 
              onClick={async () => {
                await logout();
                toast.success(t('logoutSuccess') || 'Sesión cerrada');
                navigate('/login');
              }}
              variant="outline" 
              size="sm" 
              className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{t('logout') || 'Salir'}</span>
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 
              className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('adminDashboard')}
            </h1>
            <p className="mt-2 text-slate-600">
              {t('manageAlbumsPhotos')}
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <FolderOpen className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wider font-semibold">{t('totalAlbums')}</p>
                  <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="total-albums">
                    {stats.total_albums}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wider font-semibold">{t('totalPhotos')}</p>
                  <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="total-photos">
                    {stats.total_photos}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Image Settings */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-8">
            <div className="p-6 border-b border-slate-100 flex items-center gap-2">
              <Settings className="w-5 h-5 text-slate-500" />
              <h2 
                className="text-xl font-bold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t('settings')}
              </h2>
            </div>
            <div className="p-6 space-y-8">
              {/* Photo Counter Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  {showCounter ? (
                    <Eye className="w-5 h-5 text-green-600" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-slate-400" />
                  )}
                  <div>
                    <p className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {t('photoCounter')}
                    </p>
                    <p className="text-sm text-slate-500">
                      {showCounter ? t('counterEnabled') : t('counterDisabled')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={showCounter}
                  onCheckedChange={handleToggleCounter}
                  data-testid="counter-toggle"
                />
              </div>
              
              {/* Hero Images Upload */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                    {t('heroImages')} ({heroImages.length}/3)
                  </p>
                  <input
                    ref={heroInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleUploadHeroImage}
                    className="hidden"
                    data-testid="hero-image-input"
                  />
                  <Button
                    onClick={() => heroInputRef.current?.click()}
                    disabled={uploadingHero || heroImages.length >= 3}
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    data-testid="upload-hero-btn"
                  >
                    {uploadingHero ? (
                      <span className="flex items-center gap-2">
                        <div className="spinner" />
                        {t('uploading')}...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        {t('addImage')}
                      </span>
                    )}
                  </Button>
                </div>
                
                {heroImages.length === 0 ? (
                  <div className="p-8 border-2 border-dashed border-slate-200 rounded-xl text-center">
                    <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">{t('noImagesUploaded')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {heroImages.map((img, index) => (
                      <div key={index} className="relative group rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${img}`}
                          alt={`Hero ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        {/* Move buttons */}
                        <div className="absolute top-2 left-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleMoveHeroImage(index, 'up')}
                            disabled={index === 0}
                            className={`w-7 h-7 rounded-full ${index === 0 ? 'bg-slate-300 cursor-not-allowed' : 'bg-indigo-500 hover:bg-indigo-600'} text-white flex items-center justify-center`}
                            data-testid={`move-hero-up-${index}`}
                            title={t('moveUp') || 'Mover arriba'}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleMoveHeroImage(index, 'down')}
                            disabled={index === heroImages.length - 1}
                            className={`w-7 h-7 rounded-full ${index === heroImages.length - 1 ? 'bg-slate-300 cursor-not-allowed' : 'bg-indigo-500 hover:bg-indigo-600'} text-white flex items-center justify-center`}
                            data-testid={`move-hero-down-${index}`}
                            title={t('moveDown') || 'Mover abajo'}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Delete button */}
                        <button
                          onClick={() => handleDeleteHeroImage(index)}
                          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-hero-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-xs">
                          {index + 1}/{heroImages.length}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-sm text-slate-500 mt-3">
                  Recomendado: 1200x800px o mayor. Las imágenes se mostrarán como carrusel.
                </p>
              </div>
            </div>
          </div>

          {/* Albums List */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <h2 
                className="text-xl font-bold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t('allAlbums')} ({albums.length})
              </h2>
            </div>
            
            {albums.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <FolderOpen className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-600">{t('noAlbumsCreated')}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {albums.map((album) => (
                  <div 
                    key={album.id} 
                    className="p-4 sm:p-6 hover:bg-slate-50 transition-colors"
                    data-testid={`admin-album-${album.share_code}`}
                  >
                    {/* Mobile Layout */}
                    <div className="sm:hidden space-y-3">
                      {/* Header: Icon + Name + Private badge */}
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-pink-100 flex items-center justify-center flex-shrink-0 relative">
                          <ImageIcon className="w-6 h-6 text-indigo-600" />
                          {album.is_private && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                              <Lock className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                              {album.name}
                            </p>
                            {album.is_private && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                {t('private')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            {album.photo_count}{album.photo_limit ? `/${album.photo_limit}` : ''} {album.photo_count !== 1 ? t('photos') : t('photo')}
                            {album.photo_limit === null && <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded-full">{t('unlimited')}</span>}
                          </p>
                        </div>
                      </div>
                      
                      {/* Info Grid */}
                      <div className="grid grid-cols-2 gap-2 text-xs pl-15">
                        <div className="bg-slate-50 rounded-lg p-2">
                          <span className="text-slate-400 block">{t('code')}</span>
                          <span className="text-slate-700 font-mono">{album.share_code}</span>
                        </div>
                        {album.event_date && (
                          <div className="bg-indigo-50 rounded-lg p-2">
                            <span className="text-indigo-400 block">{t('eventDate')}</span>
                            <span className="text-indigo-700">{new Date(album.event_date + 'T00:00:00').toLocaleDateString()}</span>
                          </div>
                        )}
                        {album.expiration_date && (
                          <div className="bg-red-50 rounded-lg p-2 col-span-2">
                            <span className="text-red-400 block">{t('expiresOn')}</span>
                            <span className="text-red-600">{new Date(album.expiration_date + 'T00:00:00').toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Actions Row */}
                      <div className="flex items-center justify-end gap-1 pt-2 border-t border-slate-100">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAlbumPhotos(album)}
                          className="text-slate-600 hover:text-indigo-600"
                          data-testid={`view-photos-${album.share_code}`}
                        >
                          <ImageIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/edit/${album.share_code}`)}
                          className="text-slate-600 hover:text-indigo-600"
                          data-testid={`edit-album-${album.share_code}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Link to={`/album/${album.share_code}`} target="_blank">
                          <Button variant="ghost" size="sm" className="text-slate-600 hover:text-indigo-600">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-600 hover:text-red-600"
                              data-testid={`delete-album-mobile-${album.share_code}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t('deleteAlbum')}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t('deleteAlbumDesc').replace('the album', `"${album.name}"`).replace('all its', `${album.photo_count}`)}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteAlbum(album.share_code)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                {t('delete')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    
                    {/* Desktop Layout */}
                    <div className="hidden sm:flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-pink-100 flex items-center justify-center flex-shrink-0 relative">
                          <ImageIcon className="w-6 h-6 text-indigo-600" />
                          {album.is_private && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                              <Lock className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                              {album.name}
                            </p>
                            {album.is_private && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                {t('private')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            {album.photo_count}{album.photo_limit ? `/${album.photo_limit}` : ''} {album.photo_count !== 1 ? t('photos') : t('photo')} • {t('code')}: {album.share_code}
                            {album.photo_limit === null && <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded-full">{t('unlimited')}</span>}
                          </p>
                          {album.event_date && (
                            <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {t('eventDate')}: {new Date(album.event_date + 'T00:00:00').toLocaleDateString()}
                              {album.expiration_date && (
                                <span className="ml-2 text-slate-400">
                                  • {t('albumExpiresOn')} {new Date(album.expiration_date + 'T00:00:00').toLocaleDateString()}
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAlbumPhotos(album)}
                          className="text-slate-600 hover:text-indigo-600"
                          data-testid={`view-photos-${album.share_code}`}
                        >
                          <ImageIcon className="w-4 h-4 mr-2" />
                          <span>{t('photos')}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/edit/${album.share_code}`)}
                          className="text-slate-600 hover:text-indigo-600"
                          data-testid={`edit-album-${album.share_code}`}
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          <span>{t('edit')}</span>
                        </Button>
                        <Link to={`/album/${album.share_code}`} target="_blank">
                          <Button variant="ghost" size="sm" className="text-slate-600 hover:text-indigo-600">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-600 hover:text-red-600"
                              data-testid={`delete-album-${album.share_code}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t('deleteAlbum')}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t('deleteAlbumDesc').replace('the album', `"${album.name}"`).replace('all its', `${album.photo_count}`)}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteAlbum(album.share_code)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                {t('delete')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Album Photos Dialog */}
      <Dialog open={!!selectedAlbum} onOpenChange={() => setSelectedAlbum(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {selectedAlbum?.name} - {t('photos')} ({albumPhotos.length})
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            {albumPhotos.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <ImageIcon className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-600">{t('noPhotosInAlbum')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {albumPhotos.map((photo) => (
                  <div 
                    key={photo.id} 
                    className={`relative group rounded-xl overflow-hidden bg-slate-100 ${photo.hidden ? 'ring-2 ring-amber-300' : ''} ${photo.is_official ? 'ring-2 ring-indigo-400' : ''}`}
                    data-testid={`admin-photo-${photo.id}`}
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                      alt={photo.original_filename}
                      className={`w-full h-32 object-cover ${photo.hidden ? 'opacity-60' : ''}`}
                    />
                    {photo.hidden && (
                      <div className="absolute top-2 left-2">
                        <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                          <EyeOff className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    )}
                    {photo.is_official && (
                      <div className="absolute top-2 left-2">
                        <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center">
                          <Star className="w-3 h-3 text-white fill-white" />
                        </div>
                      </div>
                    )}
                    {/* Official toggle - always visible for massive events */}
                    {selectedAlbum?.is_massive_event && (
                      <button
                        onClick={() => handleToggleOfficial(photo)}
                        className={`absolute top-2 left-2 w-8 h-8 rounded-full ${photo.is_official ? 'bg-indigo-500 hover:bg-indigo-600' : 'bg-black/50 hover:bg-indigo-500'} text-white flex items-center justify-center transition-all z-10`}
                        data-testid={`admin-official-${photo.id}`}
                        title={photo.is_official ? t('removeOfficial') : t('markAsOfficial')}
                      >
                        <Star className={`w-4 h-4 ${photo.is_official ? 'fill-white' : ''}`} />
                      </button>
                    )}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleTogglePhotoVisibility(photo)}
                        className={`w-8 h-8 rounded-full ${photo.hidden ? 'bg-green-500 hover:bg-green-600' : 'bg-amber-500 hover:bg-amber-600'} text-white flex items-center justify-center transition-all`}
                        data-testid={`admin-toggle-${photo.id}`}
                        title={photo.hidden ? t('showPhoto') : t('hidePhoto')}
                      >
                        {photo.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setPhotoToDelete(photo)}
                        className="w-8 h-8 rounded-full bg-black/50 hover:bg-red-500 text-white flex items-center justify-center transition-all"
                        data-testid={`admin-delete-photo-${photo.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Photo Confirmation Modal */}
      <AlertDialog open={!!photoToDelete} onOpenChange={() => setPhotoToDelete(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deletePhotoConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deletePhotoDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {photoToDelete && (
            <div className="my-4 rounded-xl overflow-hidden bg-slate-100">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${photoToDelete.url}`}
                alt={photoToDelete.original_filename}
                className="w-full h-48 object-contain"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeletePhoto(photoToDelete.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
